import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    X, Percent, Users, Pause, Play, Trash2, UserPlus, Copy, AlertTriangle,
    ShieldCheck, Settings,
} from "lucide-react";
import { toast } from "sonner";
import { superApi } from "../lib/admin";

/**
 * Super-admin ops drawer for a single tenant. Four tabs: Discount, Admins,
 * Status (suspend/unsuspend), Danger (soft-delete).
 *
 * All mutations call /super/* endpoints; those are audit-logged server-side.
 */
const TenantOps = ({ tenant, onClose, onChanged }) => {
    const [tab, setTab] = useState("discount");

    if (!tenant) return null;

    return (
        <AnimatePresence>
            <motion.div
                key="ops-backdrop"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-[80] bg-[#2C1A12]/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
                data-testid="tenant-ops-modal"
                onClick={onClose}
            >
                <motion.div
                    initial={{ y: 40, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 40, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 220, damping: 24 }}
                    className="w-full max-w-2xl rounded-3xl bg-white max-h-[90vh] overflow-y-auto shadow-[0_30px_60px_rgba(44,26,18,0.35)]"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="sticky top-0 bg-white border-b border-[#E8DCCB] px-6 py-4 flex items-center justify-between z-10">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="h-10 w-10 rounded-2xl bg-[#2C1A12] text-white flex items-center justify-center flex-shrink-0">
                                <Settings className="h-5 w-5" strokeWidth={2.5} />
                            </div>
                            <div className="min-w-0">
                                <div className="font-display font-black text-lg truncate">{tenant.name}</div>
                                <div className="text-xs text-[#8C7A72] font-mono">/{tenant.slug} · {tenant.subscription_status}</div>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            data-testid="tenant-ops-close"
                            className="h-9 w-9 rounded-full bg-[#F9F6F0] hover:bg-[#E8DCCB] flex items-center justify-center"
                        >
                            <X className="h-4 w-4" strokeWidth={2.5} />
                        </button>
                    </div>

                    {/* Tabs */}
                    <div className="px-4 pt-4 flex gap-1 border-b border-[#E8DCCB] overflow-x-auto">
                        {[
                            { key: "discount", label: "Discount", Icon: Percent },
                            { key: "admins", label: "Admins", Icon: Users },
                            { key: "status", label: "Status", Icon: Pause },
                            { key: "danger", label: "Danger", Icon: AlertTriangle, danger: true },
                        ].map((t) => (
                            <button
                                key={t.key}
                                onClick={() => setTab(t.key)}
                                data-testid={`tenant-ops-tab-${t.key}`}
                                className={`px-4 py-2.5 text-xs font-display font-bold uppercase tracking-wider flex items-center gap-1.5 border-b-2 whitespace-nowrap transition-colors ${
                                    tab === t.key
                                        ? (t.danger ? "border-[#B4361E] text-[#B4361E]" : "border-[#FF4F18] text-[#2C1A12]")
                                        : "border-transparent text-[#8C7A72] hover:text-[#2C1A12]"
                                }`}
                            >
                                <t.Icon className="h-3.5 w-3.5" strokeWidth={2.5} />
                                {t.label}
                            </button>
                        ))}
                    </div>

                    <div className="p-6">
                        {tab === "discount" && <DiscountTab tenant={tenant} onChanged={onChanged} />}
                        {tab === "admins" && <AdminsTab tenant={tenant} />}
                        {tab === "status" && <StatusTab tenant={tenant} onChanged={onChanged} />}
                        {tab === "danger" && <DangerTab tenant={tenant} onDeleted={() => { onChanged?.(); onClose(); }} />}
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

/* ================ DISCOUNT TAB ================ */
const DiscountTab = ({ tenant, onChanged }) => {
    const [pct, setPct] = useState(tenant.subscription_discount_bps ? tenant.subscription_discount_bps / 100 : 0);
    const [reason, setReason] = useState(tenant.subscription_discount_reason || "");
    const [until, setUntil] = useState(tenant.subscription_discount_until?.slice(0, 10) || "");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const bps = Math.round(Math.min(100, Math.max(0, Number(pct))) * 100);
            await superApi.post(`/super/tenants/${tenant.slug}/discount`, {
                discount_bps: bps,
                reason,
                until: until ? new Date(until).toISOString() : null,
            });
            toast.success(bps === 0 ? "Discount cleared" : `${pct}% discount applied`);
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setSaving(false);
        }
    };

    const sub = tenant.plan === "pro" ? 79 : 49;
    const effective = Math.max(0, sub * (1 - (pct / 100))).toFixed(2);

    return (
        <div data-testid="discount-tab">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF4F18] font-bold">Subscription discount</div>
            <h3 className="mt-1 font-display font-black text-xl">Reduce their monthly fee</h3>
            <p className="mt-1 text-sm text-[#8C7A72]">
                Applies to the Coffeein7 subscription only (not transaction fees). Zero clears any
                active discount.
            </p>

            <div className="mt-5 rounded-2xl bg-[#F9F6F0] border border-[#E8DCCB] p-4">
                <label className="text-[10px] uppercase tracking-wider text-[#8C7A72] font-semibold">Discount %</label>
                <div className="mt-1 flex items-baseline gap-3">
                    <input
                        type="number" min="0" max="100" step="5"
                        value={pct}
                        onChange={(e) => setPct(e.target.value)}
                        data-testid="discount-pct-input"
                        className="w-28 text-3xl font-display font-black bg-transparent outline-none border-b-2 border-[#E8DCCB] focus:border-[#FF4F18]"
                    />
                    <span className="text-2xl text-[#8C7A72]">%</span>
                    <div className="ml-auto text-right">
                        <div className="text-[10px] uppercase tracking-wider text-[#8C7A72]">New monthly cost</div>
                        <div className="font-display font-black text-xl text-[#FF4F18]">${effective}</div>
                        <div className="text-[10px] text-[#8C7A72]">was ${sub}</div>
                    </div>
                </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
                <label className="block">
                    <span className="text-[10px] uppercase tracking-wider text-[#8C7A72] font-semibold">Expires</span>
                    <input
                        type="date"
                        value={until}
                        onChange={(e) => setUntil(e.target.value)}
                        data-testid="discount-until-input"
                        className="mt-1 w-full px-3 py-2 rounded-xl bg-white border-2 border-[#E8DCCB] outline-none focus:border-[#FF4F18] text-sm"
                    />
                </label>
                <label className="block">
                    <span className="text-[10px] uppercase tracking-wider text-[#8C7A72] font-semibold">Reason</span>
                    <input
                        type="text"
                        placeholder="e.g. Launch promo"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        data-testid="discount-reason-input"
                        className="mt-1 w-full px-3 py-2 rounded-xl bg-white border-2 border-[#E8DCCB] outline-none focus:border-[#FF4F18] text-sm"
                        maxLength={280}
                    />
                </label>
            </div>

            <button
                onClick={save}
                disabled={saving}
                data-testid="discount-save-btn"
                className="mt-5 w-full py-3 rounded-full bg-[#2C1A12] text-white font-display font-bold disabled:opacity-60 active:scale-[0.98]"
            >
                {saving ? "Saving…" : "Save discount"}
            </button>

            <div className="mt-3 text-[11px] text-[#8C7A72]">
                Note: this sets the source-of-truth price in your café-facing financials.
                Stripe invoicing adjustment is applied via a Stripe Coupon on the next invoice —
                see follow-up tasks.
            </div>
        </div>
    );
};

/* ================ ADMINS TAB ================ */
const AdminsTab = ({ tenant }) => {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(false);
    const [name, setName] = useState("");
    const [pin, setPin] = useState("");
    const [role, setRole] = useState("staff");
    const [adding, setAdding] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await superApi.get(`/super/tenants/${tenant.slug}/admins`);
            setAdmins(r.data.admins);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenant.slug]);

    const addAdmin = async () => {
        if (!name || pin.length < 4) {
            toast.error("Name + 4-digit PIN required");
            return;
        }
        setAdding(true);
        try {
            const r = await superApi.post(`/super/tenants/${tenant.slug}/admins`, { name, pin, role });
            toast.success(`Added ${r.data.admin.name} — share PIN ${r.data.pin_once}`);
            setName(""); setPin(""); setRole("staff");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setAdding(false);
        }
    };

    const remove = async (admin) => {
        if (admin.is_owner) return;
        if (!window.confirm(`Revoke ${admin.name}'s admin access?`)) return;
        try {
            await superApi.delete(`/super/tenants/${tenant.slug}/admins/${admin.id}`);
            toast.success(`${admin.name} removed`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    return (
        <div data-testid="admins-tab">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF4F18] font-bold">Café admins</div>
            <h3 className="mt-1 font-display font-black text-xl">Who can open the admin dashboard</h3>

            <div className="mt-5 space-y-2">
                {loading && <div className="text-sm text-[#8C7A72]">Loading…</div>}
                {admins.map((a) => (
                    <div
                        key={a.id}
                        className={`rounded-2xl border p-3 flex items-center gap-3 ${
                            a.is_owner ? "bg-[#F9F6F0] border-[#E8DCCB]" : "bg-white border-[#E8DCCB]"
                        }`}
                        data-testid={`admin-row-${a.id}`}
                    >
                        <div className={`h-9 w-9 rounded-full flex items-center justify-center text-white ${a.is_owner ? "bg-[#FF4F18]" : "bg-[#2C1A12]"}`}>
                            {a.is_owner ? <ShieldCheck className="h-4 w-4" strokeWidth={2.5} /> : <Users className="h-4 w-4" strokeWidth={2.5} />}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-display font-bold truncate">{a.name}</div>
                            <div className="text-[10px] uppercase tracking-wider text-[#8C7A72]">{a.role}</div>
                        </div>
                        {!a.is_owner && (
                            <button
                                onClick={() => remove(a)}
                                data-testid={`admin-remove-${a.id}`}
                                className="text-xs font-bold text-[#B4361E] hover:underline"
                            >
                                Revoke
                            </button>
                        )}
                    </div>
                ))}
            </div>

            <div className="mt-5 rounded-2xl bg-[#F9F6F0] border border-dashed border-[#E8DCCB] p-4">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#8C7A72] font-semibold flex items-center gap-1.5">
                    <UserPlus className="h-3.5 w-3.5" strokeWidth={2.5} />
                    Add new admin
                </div>
                <div className="mt-3 grid gap-2 sm:grid-cols-[1.5fr_80px_100px_auto]">
                    <input
                        type="text" placeholder="Name"
                        value={name} onChange={(e) => setName(e.target.value)}
                        data-testid="admin-add-name"
                        className="px-3 py-2.5 rounded-xl bg-white border-2 border-[#E8DCCB] outline-none focus:border-[#FF4F18] text-sm"
                        maxLength={80}
                    />
                    <input
                        type="tel" inputMode="numeric" placeholder="PIN"
                        value={pin}
                        onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
                        data-testid="admin-add-pin"
                        className="px-3 py-2.5 rounded-xl bg-white border-2 border-[#E8DCCB] outline-none focus:border-[#FF4F18] font-mono text-sm tracking-widest text-center"
                    />
                    <select
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        data-testid="admin-add-role"
                        className="px-3 py-2.5 rounded-xl bg-white border-2 border-[#E8DCCB] outline-none text-sm"
                    >
                        <option value="staff">Staff</option>
                        <option value="manager">Manager</option>
                    </select>
                    <button
                        onClick={addAdmin}
                        disabled={adding || !name || pin.length < 4}
                        data-testid="admin-add-btn"
                        className="px-4 py-2.5 rounded-xl bg-[#FF4F18] text-white font-display font-bold text-sm disabled:opacity-40 active:scale-95"
                    >
                        Add
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ================ STATUS TAB ================ */
const StatusTab = ({ tenant, onChanged }) => {
    const [reason, setReason] = useState("");
    const [acting, setActing] = useState(false);
    const suspended = tenant.subscription_status === "suspended" || tenant.status === "suspended";

    const suspend = async () => {
        if (!window.confirm(`Suspend ${tenant.name}? Orders will refuse until unsuspended.`)) return;
        setActing(true);
        try {
            await superApi.post(`/super/tenants/${tenant.slug}/suspend`, { reason });
            toast.success("Café suspended");
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setActing(false);
        }
    };

    const unsuspend = async () => {
        setActing(true);
        try {
            const r = await superApi.post(`/super/tenants/${tenant.slug}/unsuspend`, {});
            toast.success(`Resumed — now ${r.data.subscription_status}`);
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setActing(false);
        }
    };

    return (
        <div data-testid="status-tab">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF4F18] font-bold">Café status</div>
            <h3 className="mt-1 font-display font-black text-xl">
                {suspended ? "Currently suspended" : "Currently live"}
            </h3>

            {suspended ? (
                <div className="mt-5 rounded-2xl bg-[#FFF5F0] border border-[#FF4F18]/30 p-5">
                    <p className="text-sm text-[#2C1A12]">
                        Public menu + ordering are refused. The café is NOT deleted — all data is intact.
                        Unsuspending resumes whatever subscription state they were in before.
                    </p>
                    <button
                        onClick={unsuspend}
                        disabled={acting}
                        data-testid="unsuspend-btn"
                        className="mt-4 w-full py-3 rounded-full bg-[#0E6B4C] text-white font-display font-bold disabled:opacity-60 active:scale-[0.98] flex items-center justify-center gap-2"
                    >
                        <Play className="h-4 w-4" strokeWidth={2.5} />
                        {acting ? "Resuming…" : "Resume café"}
                    </button>
                </div>
            ) : (
                <div className="mt-5 rounded-2xl bg-[#F9F6F0] border border-[#E8DCCB] p-5">
                    <p className="text-sm text-[#8C7A72]">
                        Freeze the café without deleting. All data stays — the public menu simply refuses.
                        Useful for payment disputes, abuse investigations, or holiday pauses.
                    </p>
                    <input
                        type="text"
                        placeholder="Reason (optional, audit-logged)"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        data-testid="suspend-reason"
                        className="mt-3 w-full px-3 py-2.5 rounded-xl bg-white border-2 border-[#E8DCCB] outline-none focus:border-[#FF4F18] text-sm"
                        maxLength={280}
                    />
                    <button
                        onClick={suspend}
                        disabled={acting}
                        data-testid="suspend-btn"
                        className="mt-3 w-full py-3 rounded-full bg-[#2C1A12] text-white font-display font-bold disabled:opacity-60 active:scale-[0.98] flex items-center justify-center gap-2"
                    >
                        <Pause className="h-4 w-4" strokeWidth={2.5} />
                        {acting ? "Suspending…" : "Suspend café"}
                    </button>
                </div>
            )}
        </div>
    );
};

/* ================ DANGER TAB ================ */
const DangerTab = ({ tenant, onDeleted }) => {
    const [confirmSlug, setConfirmSlug] = useState("");
    const [deleting, setDeleting] = useState(false);

    const del = async () => {
        if (confirmSlug !== tenant.slug) {
            toast.error("Slug mismatch — type it exactly to confirm");
            return;
        }
        setDeleting(true);
        try {
            await superApi.delete(`/super/tenants/${tenant.slug}?confirm=${tenant.slug}`);
            toast.success(`${tenant.name} soft-deleted`);
            onDeleted?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div data-testid="danger-tab">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#B4361E] font-bold">Danger zone</div>
            <h3 className="mt-1 font-display font-black text-xl">Soft-delete café</h3>

            <div className="mt-5 rounded-2xl bg-[#FFF5F0] border border-[#B4361E]/30 p-5">
                <p className="text-sm text-[#2C1A12]">
                    This hides <span className="font-bold">/{tenant.slug}</span> from the public and prevents
                    new orders. Historical data (fees, loyalty, audit log) is kept so refunds and
                    accounting still work. Reversible: use <span className="italic">Status → Resume</span> to
                    bring it back.
                </p>
                <div className="mt-4">
                    <div className="text-[10px] uppercase tracking-wider text-[#B4361E] font-semibold">
                        Type the café slug to confirm
                    </div>
                    <input
                        type="text"
                        placeholder={tenant.slug}
                        value={confirmSlug}
                        onChange={(e) => setConfirmSlug(e.target.value)}
                        data-testid="delete-confirm-slug"
                        className="mt-1 w-full px-3 py-2.5 rounded-xl bg-white border-2 border-[#B4361E]/30 outline-none focus:border-[#B4361E] font-mono text-sm"
                        autoComplete="off"
                    />
                </div>
                <button
                    onClick={del}
                    disabled={deleting || confirmSlug !== tenant.slug}
                    data-testid="delete-btn"
                    className="mt-4 w-full py-3 rounded-full bg-[#B4361E] text-white font-display font-bold disabled:opacity-40 active:scale-[0.98] flex items-center justify-center gap-2"
                >
                    <Trash2 className="h-4 w-4" strokeWidth={2.5} />
                    {deleting ? "Deleting…" : `Soft-delete ${tenant.slug}`}
                </button>
            </div>
        </div>
    );
};

export default TenantOps;
